import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useMenuPermission } from '@src/recoil/permission';
import { PRODUCT_KEYS } from '@src/types/productType';
import Spinner from '@components/molecules/Spinner';
import CommonCheckBox from '@src/components/molecules/checkBox/CheckBox';

import { CallGetOptGroup, CallPutOptGroup } from '@src/library/request/callProduct';

const OptionName = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const storeId = searchParams.get('storeID');
  const optionGroupID = searchParams.get('optionGroupID');
  const optionID = searchParams.get('optionID');

  const { menuPermission } = useMenuPermission(storeId);

  const [loading, setLoading] = useState(false);
  const [inputData, setInputData] = useState({
    name: '',
    price: '',
    maxSelect: '',
    alcohol: 0,
  });

  const [isChanged, setIsChanged] = useState(false);

  const { data: optDetail, refetch } = useQuery([PRODUCT_KEYS.OPTIONGROUPDETAIL, storeId, optionGroupID, optionID], {
    enabled: true,
    cacheTime: 0,
    queryFn: async () => {
      try {
        setLoading(true);
        const res = await CallGetOptGroup(storeId, optionGroupID);
        if (res.data) {
          const opt = res.data as any;

          if (optionID) {
            const child = opt.children.find((v: any) => v.optionID === Number(optionID));
            setInputData({
              name: child.name,
              price: child.price,
              maxSelect: child.maxSelect,
              alcohol: child.alcohol,
            });
          } else {
            setInputData({
              name: opt.title,
              price: '',
              maxSelect: '',
              alcohol: 0,
            });
          }

          setIsChanged(false);
        }

        return res.data as any;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  const saveOptionGroup = async () => {
    setLoading(true);

    const param = {
      options: [
        {
          optionGroupID: optDetail.ID,
          isPriceOption: optDetail.isPriceOption,
          title: optDetail.title,
          selectType: optDetail.selectType,
          isDisplay: optDetail.isDisplay,
          minSelect: optDetail.minSelect,
          maxSelect: optDetail.maxSelect,
          children: [] as any,
        },
      ],
    };
    if (optionID) {
      const child = optDetail.children.map((v: any) => {
        if (v.optionID === Number(optionID)) {
          return {
            ...v,
            name: inputData.name,
            price: Number(inputData.price),
            maxSelect: Number(inputData.maxSelect),
            alcohol: inputData.alcohol,
          };
        }
        return v;
      });
      param.options[0].children = child;
    } else {
      param.options[0].title = inputData.name;
      param.options[0].children = optDetail.children;
    }

    try {
      const res = await CallPutOptGroup(storeId, optionGroupID, param);
      if (res.result.code === 1) {
        if (optionID) {
          alert('옵션명 및 가격 수정이 완료되었습니다.');
        } else {
          alert('옵션 그룹명이 수정 완료되었습니다.');
        }
        refetch();
        queryClient.invalidateQueries([PRODUCT_KEYS.OPTIONGROUP, storeId]);
        window.history.back();
      } else alert(res.result.message);
    } catch (error) {
      console.log('error', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // isNoAuth10 로직과 유사: 정보/가격 권한 체크
  // 필드별 세밀한 제어 필요 (옵션명=정보, 가격=가격)
  const disableState = useMemo(() => {
    const state = { price: false, name: false, maxSelect: false };
    if (!menuPermission.isFetch || !menuPermission.data?.[0]) {
      return state;
    }
    const permission = menuPermission.data[0];
    const {
      permissionType, // 권한없음:0, 전체권한:1, 수정권한:2
      information, // 정보
      price, // 가격
    } = permission;
    if (permissionType === 2) {
      if (information === 1 && price === 1) return state;
      if (information === 1) {
        state.price = true; // 정보수정 경우 가격 비활성화 처리
      }
      if (price === 1) {
        // 가격수정인 경우 옵션명, 최대 선택가능 수량 설정 비활성화
        state.name = true;
        state.maxSelect = true;
      }
    }

    if (optDetail && optionID) {
      const targetChild = optDetail.children.find((v: any) => v.optionID === Number(optionID));
      if (targetChild?.menuCouponCount > 0) {
        state.price = true;
      }
    }

    return state;
  }, [menuPermission, optDetail]);

  const handleInputChange = (e: any) => {
    let { value } = e.target;
    if (e.target.name === 'name') {
      value = value.slice(0, 255);
    }

    if (e.target.name === 'price') {
      value = Number(value.replace(/,/g, ''));
    }

    if (e.target.id === 'alcohol') {
      setInputData((prev) => {
        return { ...prev, alcohol: inputData.alcohol === 0 ? 1 : 0 };
      });
      setIsChanged(true);
      return;
    }

    setInputData((prev) => {
      return { ...prev, [e.target.name]: value };
    });
    setIsChanged(true);
  };

  useEffect(() => {
    if (inputData.name === '' && optDetail) {
      const opt = optDetail as any;

      if (optionID) {
        const child = opt.children.find((v: any) => v.optionID === Number(optionID));
        setInputData({
          name: child.name,
          price: child.price,
          maxSelect: child.maxSelect,
          alcohol: child.alcohol,
        });
      } else {
        setInputData({
          name: opt.title,
          price: '',
          maxSelect: '',
          alcohol: 0,
        });
      }
    }
  }, []);

  return (
    <article id="mArticle" className="art-product">
      {/* <h3 className="screen-out">옵션명 및 가격 수정 본문</h3> */}
      {/* <div className="tip-product">
        <em className="emph-tip-product">TIP</em>
        <strong>위메프오 앱</strong>에서는 이렇게 노출됩니다.
        <a href="#none" className="link-tip-product">
          예시보기
        </a>
      </div> */}
      <div className="view-product">
        <div className="box-product">
          <strong className="lab-product">{optionID ? '옵션명' : '옵션 그룹 명'}</strong>
          <input
            type="text"
            id="name"
            name="name"
            className="tf-cont"
            placeholder="예)라면사리"
            autoComplete="off"
            value={inputData.name}
            onChange={handleInputChange}
            disabled={disableState.name}
            maxLength={255}
          />
        </div>
        {optionID && (
          <div className="box-product">
            <strong className="lab-product">가격</strong>
            {optDetail && optionID &&
              optDetail.children.find((v: any) => v.optionID === Number(optionID))?.menuCouponCount > 0 && (
              <p className="desc-product">메뉴 쿠폰이 발급된 메뉴 및 옵션은 파트너스에서 가격 수정이 불가합니다.</p>
            )}
            <label className="tf-won" htmlFor="price">
              <input
                type="tel"
                id="price"
                name="price"
                className="tf-cont"
                max="100000000"
                value={Number(inputData.price).toLocaleString()}
                onChange={handleInputChange}
                disabled={disableState.price}
              />
              <span className="txt-measure">원</span>
            </label>

            {optDetail && !optDetail.isRequired && (
              <label className="tf-won label" htmlFor="maxSelect">
                <span className="txt-measure pre">최대</span>
                <input
                  type="tel"
                  name="maxSelect"
                  className="tf-cont"
                  maxLength={2}
                  placeholder="1"
                  value={inputData.maxSelect}
                  onChange={handleInputChange}
                  disabled={disableState.maxSelect}
                />
                <span className="txt-measure">개</span>
              </label>
            )}

            {optDetail && (
              <div className="check alcohol">
                <span className="alcohol-label">주류메뉴</span>
                <CommonCheckBox id="alcohol" onChange={handleInputChange} value={Boolean(inputData.alcohol)} />
              </div>
            )}
          </div>
        )}

        <div className="fixedBtnGroup">
          <button type="button" id="btnSubmit" className="btnDone" disabled={!isChanged} onClick={saveOptionGroup}>
            저장
          </button>
        </div>
      </div>
      <Spinner show={loading} />
    </article>
  );
};

export default OptionName;
